import PropTypes from "prop-types";
import * as React from "react";
import { Toast, useToastManager } from "../themeV2";
import { generateId } from "../util";

const notificationContext = React.createContext(() => {});

export function useNotification() {
  return React.useContext(notificationContext);
}

function ToastRenderer() {
  const { toasts } = useToastManager();

  return (
    <Toast.Viewport>
      {toasts.map((toast) => (
        <Toast.Root key={toast.id} toast={toast}>
          <Toast.Content>
            <Toast.Title>{toast.title}</Toast.Title>
            {toast.description && (
              <Toast.Description>{toast.description}</Toast.Description>
            )}
          </Toast.Content>
          {toast.data?.action && (
            <Toast.Action onClick={toast.data.action.onClick}>
              {toast.data.action.label}
            </Toast.Action>
          )}
          <Toast.Close />
        </Toast.Root>
      ))}
    </Toast.Viewport>
  );
}

ToastRenderer.displayName = "ToastRenderer";

function NotifyBridge({ children }) {
  const { add, close } = useToastManager();

  const notify = React.useCallback(
    ({ message, duration, action, type, onDismiss } = {}) => {
      const id = generateId();

      // Taking the action dismisses the toast — the user has responded to it,
      // and an actionable toast is usually given `duration: 0` so it waits.
      const withDismiss = (handler) => (event) => {
        handler?.(event);
        close(id);
      };

      add({
        id,
        title: message,
        type,
        timeout: duration,
        // Fires however the toast goes away — dismissed, timed out, or because
        // the action was taken. Anything the toast was holding is released here.
        onRemove: onDismiss,
        data: action
          ? { action: { ...action, onClick: withDismiss(action.onClick) } }
          : undefined,
      });

      return () => close(id);
    },
    [add, close],
  );

  return (
    <notificationContext.Provider value={notify}>
      {children}
    </notificationContext.Provider>
  );
}

NotifyBridge.displayName = "NotifyBridge";

NotifyBridge.propTypes = {
  children: PropTypes.node,
};

const NotificationProvider = React.memo(function NotificationProvider({
  children,
}) {
  return (
    <Toast.Provider timeout={5000} limit={3}>
      <NotifyBridge>{children}</NotifyBridge>
      <ToastRenderer />
    </Toast.Provider>
  );
});

NotificationProvider.propTypes = {
  children: PropTypes.node,
};

export default NotificationProvider;
